import { useEffect, useState } from "react";
import { Bell, CheckCheck, Info, AlertTriangle, XCircle, UserX, BellOff, Send, RefreshCw, Smartphone, CalendarClock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const ICONS = { info: Info, warning: AlertTriangle, error: XCircle };

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [weeklyBusy, setWeeklyBusy] = useState(false);
  const push = usePushNotifications();

  const load = async () => {
    const { data } = await api.get("/notifications");
    setItems(data);
  };
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, []);

  const readAll = async () => { await api.post("/notifications/read-all"); load(); };

  const checkAbsences = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/check-absences");
      if (data.absent_count === 0) toast.success("Devamsız yok — herkes giriş yapmış veya izinli");
      else toast.warning(`${data.absent_count} devamsız personel tespit edildi`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const sendWeeklySummary = async () => {
    setWeeklyBusy(true);
    try {
      const { data } = await api.post("/notifications/weekly-summary");
      toast.success(`Haftalık özet gönderildi (${data.week_start} – ${data.week_end})`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setWeeklyBusy(false); }
  };

  const togglePush = async () => {
    if (push.subscribed) {
      await push.unsubscribe();
      toast.success("Push bildirimleri kapatıldı");
    } else {
      const r = await push.subscribe();
      if (r.ok) toast.success("Push bildirimleri açıldı — telefon ana ekrana ekli olmalı (iOS için)");
      else if (r.reason === "denied") toast.error("Bildirim izni reddedildi. Tarayıcı ayarlarından açabilirsiniz.");
      else if (r.reason === "unsupported") toast.error("Bu tarayıcı push bildirim desteklemiyor");
      else toast.error("Push açılamadı: " + r.reason);
    }
  };

  const sendTest = async () => {
    try {
      const { data } = await push.sendTest();
      if (data.sent > 0) {
        toast.success(`Test gönderildi (${data.sent}/${data.total} cihaza)`);
      } else if (data.errors && data.errors.length) {
        toast.error(`Test başarısız: ${data.errors[0]}`, { duration: 6000 });
      } else {
        toast.warning(`Hiçbir cihaza ulaşılamadı (${data.total} abone)`, { duration: 5000 });
      }
      push.refreshDeviceCount();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const refreshSub = async () => {
    const r = await push.refresh();
    if (r.ok) {
      toast.success("Abonelik yenilendi — yeni VAPID anahtarı kaydedildi");
    } else if (r.reason === "denied") {
      toast.error("Bildirim izni reddedildi");
    } else {
      toast.error("Yenileme başarısız: " + r.reason);
    }
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Bildirimler</h1>
          <p className="text-muted-foreground text-sm mt-1">Canlı hareket ve güvenlik bildirimleri · Devamsızlık kontrolü her gün 08:15 · Haftalık mesai özeti her Pazartesi 09:00</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={checkAbsences} disabled={busy} className="gap-2" data-testid="check-absences">
            <UserX className="w-4 h-4" /> {busy ? "Kontrol ediliyor..." : "Şimdi Yoklama Al"}
          </Button>
          <Button variant="outline" onClick={sendWeeklySummary} disabled={weeklyBusy} className="gap-2" data-testid="send-weekly-summary">
            <CalendarClock className="w-4 h-4" /> {weeklyBusy ? "Gönderiliyor..." : "Haftalık Özet Gönder"}
          </Button>
          <Button variant="outline" onClick={readAll} className="gap-2" data-testid="mark-all-read">
            <CheckCheck className="w-4 h-4" /> Tümünü Okundu
          </Button>
        </div>
      </div>

      {push.supported && (
        <div className="glass-card rounded-xl p-4 space-y-3" data-testid="push-controls">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${push.subscribed ? "bg-blue-500/15 text-blue-500" : "bg-secondary text-muted-foreground"}`}>
                {push.subscribed ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
              </div>
              <div>
                <div className="font-semibold flex items-center gap-2">
                  {push.subscribed ? "Push bildirim aktif" : "Push bildirimleri kapalı"}
                  {push.deviceCount > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-xs font-mono bg-blue-500/15 text-blue-500 px-2 py-0.5 rounded-md"
                      data-testid="push-device-count"
                    >
                      <Smartphone className="w-3 h-3" /> {push.deviceCount} cihaz
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {push.subscribed
                    ? "Telefon/tarayıcı uyku modundayken bile bildirim alırsınız."
                    : "Devamsızlık, geç kalma ve güvenlik uyarılarını anlık almak için açın."}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {push.subscribed && (
                <>
                  <Button variant="outline" onClick={sendTest} className="gap-2" data-testid="push-test">
                    <Send className="w-4 h-4" /> Test Gönder
                  </Button>
                  <Button
                    variant="outline"
                    onClick={refreshSub}
                    disabled={push.busy}
                    className="gap-2"
                    data-testid="push-refresh"
                  >
                    <RefreshCw className={`w-4 h-4 ${push.busy ? "animate-spin" : ""}`} />
                    Aboneliği Yenile
                  </Button>
                </>
              )}
              <Button
                onClick={togglePush}
                disabled={push.busy}
                variant={push.subscribed ? "outline" : "default"}
                className="gap-2"
                data-testid="push-toggle"
              >
                {push.subscribed ? <><BellOff className="w-4 h-4" /> Kapat</> : <><Bell className="w-4 h-4" /> Bildirimleri Aç</>}
              </Button>
            </div>
          </div>
          {push.subscribed && (
            <div className="text-[11px] text-muted-foreground border-t border-border/40 pt-2 leading-relaxed">
              <b>Bildirim gelmiyor mu?</b> Deploy sonrası VAPID anahtarları değişmiş olabilir —
              <b> Aboneliği Yenile</b> ile taze abonelik oluşturun. Test bildirimi telefonunuza
              düşmüyorsa PWA&apos;yı kaldırıp yeniden kurmanız gerekebilir.
            </div>
          )}
        </div>
      )}

      <div className="glass-card rounded-xl divide-y divide-border/60">
        {items.map((n) => {
          const Ico = ICONS[n.type] || Bell;
          const tone = n.type === "error" ? "text-red-500" : n.type === "warning" ? "text-amber-500" : "text-blue-500";
          return (
            <div key={n.id} className={`p-4 flex items-start gap-3 ${!n.read ? "bg-secondary/30" : ""}`} data-testid={`notif-${n.id}`}>
              <div className={`w-9 h-9 rounded-lg bg-secondary flex items-center justify-center ${tone}`}>
                <Ico className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{n.message}</div>
                <div className="text-xs text-muted-foreground mt-1 font-mono">
                  {new Date(n.created_at).toLocaleString("tr-TR")}
                </div>
              </div>
              {!n.read && <span className="w-2 h-2 rounded-full bg-blue-500 mt-3" />}
            </div>
          );
        })}
        {items.length === 0 && <div className="p-10 text-center text-muted-foreground">Bildirim yok.</div>}
      </div>
    </div>
  );
}
